import { api } from './api.js';
import { installSessionHandler } from './session.js';
import { logTerminalCommand } from './activity-log.js';
import {
  todayISO, fmtDate, esc, isViewer, fmtAmt, STATUS_LABELS, TZ, leadOutstandingBalance, hasPerm, canDeleteLead,
  isClosedStatus,
} from './utils.js';
import { getNextStatus } from './ux.js';
import { isCompletedStatus } from './status-pipelines.js';
import { formatStageHistoryDescription } from './status-stage.js';
import {
  activeStageSteps,
  buildStatusPatchBody,
  validateStageAnswers,
  statusDisplayName,
  applyUnansweredStepDefaults,
  leadHasDueBalance,
  missingStageSteps,
} from './assistant-stages.js';
import {
  initNewTicketData,
  activeNewTicketSteps,
  buildNewTicketLeadBody,
  formatNewTicketSummary,
  applyNewTicketStep,
  isYes,
  isNo,
  isSkip,
} from './assistant-ticket-new.js';
import {
  initVisaData,
  activeVisaSteps,
  buildVisaLeadBody,
  formatVisaSummary,
} from './assistant-visa-new.js';
import {
  initTourData,
  activeTourSteps,
  buildTourLeadBody,
  formatTourSummary,
} from './assistant-tour-new.js';
import { setTerminalFlowOverrides } from './terminal-flow-merge.js';
import { setTerminalFlowValidationRules } from './terminal-flow-validation.js';
import { setTerminalFlowCustomSteps } from './terminal-flow-custom.js';
import { parseSmartDate, DATE_HINT, DATETIME_HINT, nowDisplayDate, nowDisplayDateTime } from './assistant-datetime.js';
import {
  describeSearchFilters,
  searchPageLabel,
  fetchAssistantSearchResults,
  SEARCH_PAGE_SIZE,
} from './assistant-search.js';
import {
  formatLeadBriefRich,
  formatLeadDetailHtml,
  formatLeadSearchBlock,
} from './assistant-lead-format.js';
import {
  editMenuText,
  editMenuMaxChoice,
  parseEditChoice,
  buildLeadUpdateBody,
  passengerPickMenuText,
  parsePassengerPick,
  passengerFieldMenuText,
  parsePassengerFieldPick,
  passengerFieldPrompt,
  validatePassengerFieldValue,
  parsePassengerFieldValue,
  applyPassengerFieldEdit as buildPassengerFieldChanges,
  buildPassengerRemovalChanges,
  canRemovePassengerTarget,
  passengerRemoveConfirmText,
  validateSimpleField,
  parseSimpleField,
  simpleFieldPrompt,
  listPassengerTargets,
  listPassengerFieldChoices,
  notInterestedSteps,
  buildNotInterestedPatchBody,
  validateNotInterestedAnswers,
} from './assistant-edit.js';
import { buildHistoryItems, formatHistoryTime } from './assistant-history.js';
import {
  callLogSteps,
  buildCallLogBody,
  validateCallLogAnswers,
} from './assistant-call-log.js';
import {
  formatPricingSummaryText,
  pricingMenuText,
  parsePricingPick,
  pricingFieldMenuText,
  parsePricingFieldPick,
  pricingValuePrompt,
  validatePricingValue,
  parsePricingValue,
  buildPricingFieldChanges,
  buildPricingHistoryNote,
  capturePricingSnapshot,
  previewLeadAfterPricingChanges,
  listPricingFieldChoices,
} from './assistant-pricing.js';
import { renderAssistantGui } from './assistant-gui.js';
import {
  initAssistantSearchPopup,
  openAssistantSearchPopup,
  closeAssistantSearchPopup,
} from './assistant-search-popup.js';
import {
  initAssistantSplit,
  toggleSplit,
  setSplitOpen,
  openSplitLeadsSearch,
  isSplitOpen,
  parseSplitCommand,
} from './assistant-split.js?v=136';
import { tryKnowledgeAnswer, fetchKnowledgeEntries } from './assistant-knowledge.js';
import { formatWelcomeHtml, pickDailyTip, formatBootStatsLine } from './assistant-welcome.js';

document.documentElement.classList.add('terminal', 'assistant-page');

installSessionHandler({ redirect: '/', message: 'Session expired — sign in again' });

const $ = (id) => document.getElementById(id);

const CMD_HISTORY_KEY = 'mysky_assistant_cmd_history';
const CMD_HISTORY_MAX = 50;

/** @type {string[]} */
let commandHistory = [];
let historyBrowseIndex = -1;
let historyDraft = '';
let historyProgrammatic = false;

function loadCommandHistory() {
  try {
    const raw = localStorage.getItem(CMD_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    commandHistory = Array.isArray(parsed)
      ? parsed.filter((s) => typeof s === 'string' && s.trim()).slice(-CMD_HISTORY_MAX)
      : [];
  } catch {
    commandHistory = [];
  }
}

function saveCommandHistory() {
  try {
    localStorage.setItem(CMD_HISTORY_KEY, JSON.stringify(commandHistory.slice(-CMD_HISTORY_MAX)));
  } catch { /* quota / private mode */ }
}

function pushCommandHistory(text) {
  const trimmed = text.trim();
  if (!trimmed) return;
  if (commandHistory.length && commandHistory[commandHistory.length - 1] === trimmed) return;
  commandHistory.push(trimmed);
  if (commandHistory.length > CMD_HISTORY_MAX) {
    commandHistory = commandHistory.slice(-CMD_HISTORY_MAX);
  }
  saveCommandHistory();
  historyBrowseIndex = -1;
  historyDraft = '';
}

function navigateCommandHistory(direction) {
  const input = $('assistantInput');
  if (!input || input.disabled || !commandHistory.length) return;

  let nextValue = input.value;

  if (direction === 'up') {
    if (historyBrowseIndex === -1) {
      historyDraft = input.value;
      historyBrowseIndex = commandHistory.length - 1;
    } else if (historyBrowseIndex > 0) {
      historyBrowseIndex -= 1;
    }
    nextValue = commandHistory[historyBrowseIndex];
  } else if (historyBrowseIndex !== -1) {
    if (historyBrowseIndex < commandHistory.length - 1) {
      historyBrowseIndex += 1;
      nextValue = commandHistory[historyBrowseIndex];
    } else {
      historyBrowseIndex = -1;
      nextValue = historyDraft;
      historyDraft = '';
    }
  }

  historyProgrammatic = true;
  input.value = nextValue;
  const len = nextValue.length;
  input.setSelectionRange(len, len);
  historyProgrammatic = false;
}

function resetCommandHistoryBrowse() {
  historyBrowseIndex = -1;
  historyDraft = '';
}

function onAssistantInputKeydown(e) {
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    navigateCommandHistory('up');
    return;
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    navigateCommandHistory('down');
    return;
  }

  const mod = e.ctrlKey || e.metaKey;
  if (mod && e.key === ';') {
    e.preventDefault();
    insertAssistantInput(e.shiftKey ? nowDisplayDate() : nowDisplayDateTime());
  }
}

function insertAssistantInput(text) {
  const input = $('assistantInput');
  if (!input || input.disabled || !text) return;
  resetCommandHistoryBrowse();
  historyProgrammatic = true;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const before = input.value.slice(0, start);
  const after = input.value.slice(end);
  let insert = text;
  if (before && !/\s$/.test(before)) insert = ` ${insert}`;
  if (after && !/^\s/.test(after)) insert = `${insert} `;
  input.value = before + insert + after;
  const pos = (before + insert).length;
  input.setSelectionRange(pos, pos);
  historyProgrammatic = false;
  input.focus();
}

const DEFAULT_SOURCES = ['Call', 'Personal', 'WhatsApp', 'Facebook', 'Instagram', 'Walk-in', 'Referral', 'Website', 'Other'];

const state = {
  agent: null,
  sources: DEFAULT_SOURCES,
  mode: 'menu',
  step: 0,
  data: null,
  saving: false,
  savedLeadId: null,
  searchResults: [],
  searchPick: null,
  searchRawQuery: '',
  searchParsed: null,
  searchPage: 1,
  searchTotal: 0,
  searchTotalPages: 1,
  pendingSearchQuery: null,
  lead: null,
  pendingStatus: null,
  managePhase: null,
  stageAnswers: {},
  stageStepList: null,
  missingOnly: false,
  manageStep: 0,
  leadAccess: null,
  editField: null,
  editPaxTarget: null,
  editPaxField: null,
  notInterestedStep: 0,
  callLogStep: 0,
  callLogAnswers: {},
  callLogResume: null,
  pricingTarget: null,
  pricingField: null,
  pricingResume: null,
  duePayScheduled: false,
  guiEnabled: false,
  captureType: 'ticket',
  tourPackages: [],
  visaPackages: [],
  visaCosts: {},
  bootStats: null,
};

const guiCtx = {
  currentNewTicketStep: () => currentNewTicketStep(),
  currentManageStep: () => currentManageStep(),
  currentNotInterestedStep: () => currentNotInterestedStep(),
  currentCallLogStep: () => currentCallLogStep(),
  nextStatus: () => getNextStatus(state.lead?.type || 'ticket', state.lead?.status),
  canDeleteCurrentLead: () => canDeleteCurrentLead(),
  passengerFieldChoices: (target, lead) => listPassengerFieldChoices(target, lead),
  passengerFieldPrompt: (target, fieldId, lead) => passengerFieldPrompt(target, fieldId, lead),
  canRemovePassengerTarget: (lead, target) => canRemovePassengerTarget(lead, target),
  listPricingTargets: (lead) => listPricingTargets(lead),
  pricingValuePrompt: (target, fieldId, lead) => pricingValuePrompt(target, fieldId, lead),
  tourHasBalanceDue: () => tourHasBalanceDue(),
};

function refreshGuiIfEnabled() {
  if (!state.guiEnabled) return;
  renderAssistantGui($('assistantGuiBody'), state, guiCtx, (value) => {
    dispatchInput(value, { fromGui: true });
  });
}

function finishSaving() {
  state.saving = false;
  refreshGuiIfEnabled();
}

function parseGuiCommand(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'gui on' || s === 'gui enable') return 'on';
  if (s === 'gui off' || s === 'gui disable' || s === 'gui close') return 'off';
  return null;
}

function setGuiEnabled(on, { silent = false } = {}) {
  state.guiEnabled = on;
  localStorage.setItem('mysky_assistant_gui', on ? '1' : '0');
  $('assistantApp')?.classList.toggle('gui-open', on);
  $('assistantGuiPanel')?.classList.toggle('hidden', !on);
  if (!silent) {
    appendLine(on ? 'GUI panel on. Type GUI OFF to hide.' : 'GUI panel off.', 'dim');
  }
  refreshGuiIfEnabled();
}

function workingStatus() {
  return state.pendingStatus || state.lead?.status;
}

function normalizePhone(raw) {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (!s.startsWith('+')) {
    if (/^0/.test(s)) s = '+94 ' + s.replace(/^0+/, '');
    else s = '+94 ' + s;
  }
  return s.replace(/\s+/g, ' ').trim();
}

function defaultFollowupISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

function ticketHelpers() {
  return {
    sources: state.sources,
    normalizePhone,
    defaultFollowupISO,
    matchSource,
    formatSourceQuestion,
    visaPackages: state.visaPackages,
    visaCosts: state.visaCosts,
    tourPackages: state.tourPackages,
  };
}

function captureSteps() {
  if (state.captureType === 'visa') return activeVisaSteps(state.data, ticketHelpers());
  if (state.captureType === 'tour') return activeTourSteps(state.data, ticketHelpers());
  return activeNewTicketSteps(state.data, ticketHelpers());
}

function currentCaptureStep() {
  return captureSteps()[state.step] || null;
}

function classifySearchQuery(raw) {
  const s = String(raw || '').trim();
  const compact = s.replace(/\s+/g, '');
  const digits = compact.replace(/\D/g, '');
  if (digits.length >= 9 && digits.length / compact.length > 0.7) {
    return { kind: 'phone', query: s };
  }
  if (/^[A-Z0-9]{6,12}$/i.test(compact)) {
    return { kind: 'passport', query: compact.toUpperCase() };
  }
  return { kind: 'name', query: s };
}

function isIgCommand(text) {
  const s = String(text || '').trim().toLowerCase();
  return s === 'ig' || s === 'restart';
}

function formatSourceQuestion() {
  const list = state.sources.slice(0, 8).map((s, i) => `${i + 1}. ${s}`).join('  ');
  return `Lead source?\n${list}`;
}

function matchSource(raw) {
  const s = String(raw || '').trim();
  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= state.sources.length) {
    return state.sources[num - 1];
  }
  const exact = state.sources.find((x) => x.toLowerCase() === s.toLowerCase());
  if (exact) return exact;
  return s;
}

function matchPackage(raw, type) {
  const s = String(raw || '').trim();
  const num = Number(s);
  const list = type === 'visa'
    ? (state.visaPackages.length
      ? state.visaPackages
      : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa'])
    : (state.tourPackages.length
      ? state.tourPackages
      : ['Vietnam', 'India', 'Dubai', 'Bangkok', 'Singapore', 'Malaysia']);
  if (Number.isInteger(num) && num >= 1 && num <= list.length) return list[num - 1];
  const exact = list.find((x) => x.toLowerCase() === s.toLowerCase());
  if (exact) return exact;
  return s;
}

function ticketFlowSteps() {
  return captureSteps();
}

function currentNewTicketStep() {
  return currentCaptureStep();
}

function appendLine(text, className = 'system') {
  const el = document.createElement('div');
  el.className = `assistant-line ${className}`;
  el.textContent = text;
  $('assistantTranscript')?.appendChild(el);
  el.scrollIntoView({ behavior: 'smooth', block: 'end' });
}

function appendHtml(html, className = 'system') {
  const el = document.createElement('div');
  el.className = `assistant-line ${className}`;
  el.innerHTML = html;
  $('assistantTranscript')?.appendChild(el);
  el.scrollIntoView({ behavior: 'smooth', block: 'end' });
}

function clearTranscript() {
  const el = $('assistantTranscript');
  if (el) el.innerHTML = '';
}

function nowColombo() {
  return new Date().toLocaleString('en-LK', {
    timeZone: TZ,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function applyAssistantThemeClass(theme) {
  const root = document.documentElement;
  root.classList.remove('assistant-light', 'assistant-vibrant', 'assistant-classic');
  if (theme === 'light') root.classList.add('assistant-light');
  else if (theme === 'vibrant') root.classList.add('assistant-vibrant');
  else if (theme === 'classic') root.classList.add('assistant-classic');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const colors = {
      light: '#f4f4f5',
      vibrant: '#0d0221',
      classic: '#050805',
      sky: '#060d18',
    };
    meta.content = colors[theme] || colors.sky;
  }
}

function themeLabel(theme) {
  if (theme === 'light') return 'Light';
  if (theme === 'vibrant') return 'Vibrant neon';
  if (theme === 'classic') return 'Classic green';
  return 'Sky aurora';
}

function parseThemeCommand(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (/^(white|light)$/.test(s)) return 'light';
  if (/^(vibrant|neon|cyber|purple|pink|synth|magenta)$/.test(s)) return 'vibrant';
  if (/^(green|dark|terminal|classic)$/.test(s)) return 'classic';
  if (/^(sky|aurora|blue|default)$/.test(s)) return 'sky';
  return null;
}

function setAssistantTheme(mode) {
  applyAssistantThemeClass(mode);
  localStorage.setItem('mysky_assistant_theme', mode);
  const hints = {
    light: 'Type "theme sky", "theme green", or "theme vibrant".',
    vibrant: 'Type "theme sky", "theme green", or "theme white".',
    classic: 'Type "theme sky", "theme white", or "theme vibrant".',
    sky: 'Type "theme white", "theme vibrant", or "theme green".',
  };
  appendLine(`${themeLabel(mode)} theme on. ${hints[mode] || hints.sky}`, 'dim');
}

function welcomeMessage({ stats } = {}) {
  const tip = pickDailyTip(state.agent?.id, state.agent?.name);
  const statsLine = formatBootStatsLine(stats ?? state.bootStats);
  appendHtml(formatWelcomeHtml({
    agentName: state.agent?.name,
    tip,
    timeLabel: nowColombo(),
    statsLine,
  }), 'welcome');
  refreshGuiIfEnabled();
}

function helpMessage() {
  appendLine('mySky terminal — all commands', 'dim');
  appendLine('', 'dim');

  appendLine('Session & display', 'dim');
  appendLine('  help  ?              — this list', 'dim');
  appendLine('  clear                — clear the screen', 'dim');
  appendLine('  stats  status        — your numbers this month', 'dim');
  appendLine('  ig  restart          — back to main menu / cancel', 'dim');
  appendLine('  GUI ON  GUI OFF      — show or hide side panel', 'dim');
  appendLine('  split  split on/off  — leads & web beside terminal', 'dim');
  appendLine('  theme sky            — aurora sky (default)', 'dim');
  appendLine('  theme white          — light theme', 'dim');
  appendLine('  theme vibrant        — neon theme', 'dim');
  appendLine('  theme green          — classic matrix green', 'dim');
  appendLine('  ↑ ↓                  — previous / next command in input', 'dim');
  appendLine('  Ctrl+;                — insert current date & time (YYYY:MM:DD HH:MM)', 'dim');
  appendLine('  Ctrl+Shift+;          — insert current date only (YYYY:MM:DD)', 'dim');
  appendLine('', 'dim');

  appendLine('New inquiries', 'dim');
  appendLine('  new ticket           — record air ticket inquiry', 'dim');
  appendLine('  new visa             — record visa inquiry', 'dim');
  appendLine('  new tour             — record tour package inquiry', 'dim');
  appendLine('  summary              — show current capture draft', 'dim');
  appendLine('  Y / N                — confirm steps when asked', 'dim');
  appendLine('  skip  -              — skip optional fields', 'dim');
  appendLine(`  Dates ${DATE_HINT}`, 'dim');
  appendLine(`  Times HHMM (max 23:59) · Date+time ${DATETIME_HINT}`, 'dim');
  appendLine('', 'dim');

  appendLine('Search (10 results per page)', 'dim');
  appendLine('  /text john           — search name, phone, passport', 'dim');
  appendLine('  search john          — same (without slash prefix)', 'dim');
  appendLine('  Pick 1 terminal or 2 full leads page when searching', 'dim');
  appendLine('  /tickets /text john  — combine filters + text', 'dim');
  appendLine('  /tickets /ticket /tkt /air /flight', 'dim');
  appendLine('  /visa /visas', 'dim');
  appendLine('  /tour /tours /package /holiday', 'dim');
  appendLine('  /mine /my /me        — my leads only', 'dim');
  appendLine('  /madush              — filter by agent name', 'dim');
  appendLine('  /agent:name /by:name — filter by agent (explicit)', 'dim');
  appendLine('  1–10                 — select result on this page', 'dim');
  appendLine('  open  open 3         — open selected / row 3', 'dim');
  appendLine('  MD  next             — next page', 'dim');
  appendLine('  MU  prev             — previous page', 'dim');
  appendLine('', 'dim');

  appendLine('Open inquiry — manage', 'dim');
  appendLine('  summary              — show inquiry summary', 'dim');
  appendLine('  history              — full inquiry timeline', 'dim');
  appendLine('  call                 — log call (date, time, via, notes)', 'dim');
  appendLine('  pricing              — view / edit cost, profit & totals', 'dim');
  appendLine('  edit                 — edit inquiry fields (if allowed)', 'dim');
  appendLine('  request edit         — ask owner for edit access', 'dim');
  appendLine('  delete               — remove inquiry (if allowed)', 'dim');
  appendLine('  collect balance      — record tour balance payment', 'dim');
  appendLine('  Y / N                — advance to next stage when asked', 'dim');
  appendLine('  save  retry          — retry stage save if it failed', 'dim');
  appendLine('', 'dim');

  appendLine('Edit menu (type edit — options depend on inquiry type)', 'dim');
  appendLine('  Ticket: name, phone, note, pnr, source, airline, passengers', 'dim');
  appendLine('  Visa: name, phone, note, source, package, passport', 'dim');
  appendLine('  Tour: name, phone, note, source, package, duration, custom', 'dim');
  appendLine('  done                 — return to stage questions', 'dim');
  appendLine('', 'dim');

  appendLine('Tips', 'dim');
  appendLine('  Any /command starts a search · combine filters freely', 'dim');
  appendLine('  Enter on date/time prompts keeps the suggested default', 'dim');
}

function showSummary(className = 'summary') {
  if (state.captureType === 'visa') appendHtml(formatVisaSummary(state.data).replace(/\n/g, '<br>'), className);
  else if (state.captureType === 'tour') appendHtml(formatTourSummary(state.data).replace(/\n/g, '<br>'), className);
  else appendHtml(formatNewTicketSummary(state.data), className);
}

function formatLeadBrief(lead) {
  return formatLeadBriefRich(lead);
}

function formatLeadSummary(lead, access) {
  return formatLeadDetailHtml(lead, { access });
}

async function showBootBriefing() {
  let stats = null;
  try {
    stats = await api('/api/stats');
    state.bootStats = stats;
  } catch { /* ignore */ }

  appendLine(`Signed in as ${state.agent.name}`, 'success');
  welcomeMessage({ stats });
}

async function showAgentStats() {
  appendLine(`Agent: ${state.agent.name}`, 'dim');
  appendLine(`Time: ${nowColombo()}`, 'dim');
  try {
    const stats = await api('/api/stats');
    const mc = stats.month_completed || {};
    appendLine(`Month completed — tickets ${mc.ticket || 0}, visa ${mc.visa || 0}, tours ${mc.tour || 0}`);
    appendLine(`Pipeline: ${stats.total || 0} open · ${stats.overdue || 0} overdue · ${stats.completed || 0} all-time done`);
    const bt = stats.by_type || {};
    appendLine(`Mix: ${bt.ticket || 0} tickets · ${bt.visa || 0} visa · ${bt.tour || 0} tours`, 'dim');
  } catch (e) {
    appendLine(e.message || 'Could not load stats.', 'error');
  }
}

function clearManageState() {
  state.lead = null;
  state.leadAccess = null;
  state.pendingStatus = null;
  state.managePhase = null;
  state.stageAnswers = {};
  state.stageStepList = null;
  state.missingOnly = false;
  state.manageStep = 0;
  state.searchResults = [];
  state.searchPick = null;
  state.searchRawQuery = '';
  state.searchParsed = null;
  state.searchPage = 1;
  state.searchTotal = 0;
  state.searchTotalPages = 1;
  state.editField = null;
  state.editPaxTarget = null;
  state.editPaxField = null;
  state.notInterestedStep = 0;
  state.callLogStep = 0;
  state.callLogAnswers = {};
  state.callLogResume = null;
  state.pricingTarget = null;
  state.pricingField = null;
  state.pricingResume = null;
  state.duePayScheduled = false;
}

function resetSession() {
  state.mode = 'menu';
  state.step = 0;
  state.data = null;
  state.captureType = 'ticket';
  finishSaving();
  clearManageState();
  appendLine('Session cleared.', 'dim');
  welcomeMessage();
}

async function searchInquiries(rawQuery, page = 1) {
  return fetchAssistantSearchResults(rawQuery, page, {
    api,
    agentId: state.agent?.id,
    viewAll: hasPerm(state.agent, 'view_all'),
    normalizePhone,
    classifySearchQuery,
  });
}

function extractSearchQuery(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/')) return trimmed;
  const searchMatch = trimmed.match(/^(?:search|find|lookup)\s+(.+)$/i);
  if (searchMatch) return searchMatch[1].trim();
  return null;
}

function promptSearchMode(query) {
  state.pendingSearchQuery = String(query || '').trim();
  if (!state.pendingSearchQuery) return;
  state.mode = 'search_choice';
  appendLine(`Search: ${state.pendingSearchQuery}`, 'system');
  appendLine('How do you want to search?', 'system');
  appendLine('  1 Terminal — results in this screen', 'dim');
  appendLine('  2 Split screen — leads panel filtered on the right', 'dim');
  appendLine('  ig — cancel', 'dim');
  refreshGuiIfEnabled();
}

function handleSearchChoiceInput(text) {
  const s = text.trim().toLowerCase();
  const query = state.pendingSearchQuery;
  if (isIgCommand(text)) {
    state.pendingSearchQuery = null;
    state.mode = 'menu';
    appendLine('Search cancelled.', 'dim');
    welcomeMessage();
    return;
  }
  if (!query) {
    state.mode = 'menu';
    welcomeMessage();
    return;
  }
  if (s === '1' || s === 'terminal') {
    state.pendingSearchQuery = null;
    startSearch(query);
    return;
  }
  if (s === '2' || s === 'gui' || s === 'popup') {
    state.pendingSearchQuery = null;
    state.mode = 'menu';
    closeAssistantSearchPopup();
    openSplitLeadsSearch(query, {
      agentId: state.agent?.id,
      viewAll: hasPerm(state.agent, 'view_all'),
    });
    appendLine('Split screen: filtered leads on the right · manage inquiries in the terminal.', 'dim');
    refreshGuiIfEnabled();
    return;
  }
  appendLine('Pick 1 for terminal or 2 for full leads page · ig to cancel.', 'dim');
}

function printSearchResults({ results, parsed, total, totalPages, page }, { heading = true } = {}) {
  const filters = describeSearchFilters(parsed);
  const pageInfo = searchPageLabel(page, totalPages, total);
  if (heading) {
    appendLine(`Found ${pageInfo} (${filters}):`);
  } else {
    appendLine(`—— ${pageInfo} (${filters}) ——`, 'dim');
  }
  if (!results.length) {
    appendLine('No inquiries on this page.', 'error');
    return;
  }
  results.forEach((lead, i) => {
    appendLine(`${i + 1}. ${formatLeadBrief(lead)}`);
    appendHtml(formatLeadSearchBlock(lead), 'summary');
  });
  if (totalPages > 1) {
    appendLine('Type 1–10 then open · MD next page · MU prev page · ig menu.', 'dim');
  } else {
    appendLine('Type number then open to manage · or open 2 · ig to menu.', 'dim');
  }
}

async function showSearchPage(page, { heading = true } = {}) {
  if (!state.searchRawQuery) return;
  appendLine('Loading…', 'dim');
  const data = await searchInquiries(state.searchRawQuery, page);
  if (data.error) {
    appendLine(data.error, 'error');
    return;
  }
  state.searchResults = data.results;
  state.searchParsed = data.parsed;
  state.searchPage = data.page;
  state.searchTotal = data.total;
  state.searchTotalPages = data.totalPages;
  state.searchPick = null;
  state.mode = 'search';
  printSearchResults(data, { heading });
  refreshGuiIfEnabled();
}

async function startSearch(query) {
  state.searchRawQuery = String(query || '').trim();
  state.searchPage = 1;
  appendLine('Searching…', 'dim');
  const data = await searchInquiries(state.searchRawQuery, 1);
  if (data.error) {
    appendLine(data.error, 'error');
    state.mode = 'menu';
    refreshGuiIfEnabled();
    return;
  }
  state.searchParsed = data.parsed;
  state.searchTotal = data.total;
  state.searchTotalPages = data.totalPages;
  state.searchResults = data.results;
  if (!data.results.length && data.total === 0) {
    appendLine('No inquiries found.', 'error');
    appendLine(`Filters: ${describeSearchFilters(data.parsed)}`, 'dim');
    state.mode = 'menu';
    refreshGuiIfEnabled();
    return;
  }
  state.mode = 'search';
  state.searchPick = null;
  printSearchResults(data);
  refreshGuiIfEnabled();
}

function openSearchResult(index) {
  const lead = state.searchResults[index];
  if (!lead?.id) return;
  openLeadForManage(lead.id);
}

function canDeleteCurrentLead() {
  return canDeleteLead(state.agent, state.lead, state.leadAccess);
}

function tourHasBalanceDue() {
  return state.lead?.type === 'tour' && leadOutstandingBalance(state.lead) > 0;
}

function startTourBalanceCollect() {
  if (!tourHasBalanceDue()) {
    appendLine('No outstanding tour balance on this inquiry.', 'dim');
    return;
  }
  const due = leadOutstandingBalance(state.lead);
  appendHtml(`Collect balance: <span class="due-text">${esc(fmtAmt(due))}</span> outstanding`, 'system');
  appendLine('Enter amount received (LKR).', 'dim');
  state.managePhase = 'tour_balance';
  refreshGuiIfEnabled();
}

async function recordTourBalanceCollection(amount) {
  const due = leadOutstandingBalance(state.lead);
  const pay = Math.min(Math.round(amount), due);
  if (pay <= 0) {
    appendLine('Enter a valid amount.', 'error');
    return;
  }
  state.saving = true;
  appendLine('Recording payment…', 'dim');
  try {
    const res = await api(`/api/leads/${state.lead.id}/collect-balance`, {
      method: 'POST',
      body: { amount: pay },
    });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    appendLine(`Balance collected: ${fmtAmt(pay)}`, 'success');
    const remain = leadOutstandingBalance(state.lead);
    if (res.auto_advanced) {
      appendLine('Fully paid — advanced to TKT & visa stage.', 'success');
      state.managePhase = 'questions';
      state.stageAnswers = {};
      state.manageStep = 0;
      routeManageOnOpen();
    } else if (remain > 0) {
      appendHtml(`Remaining: <span class="due-text">${esc(fmtAmt(remain))}</span>`, 'error');
      state.managePhase = 'tour_balance';
      appendLine('Enter another amount or type ig.', 'dim');
    } else {
      routeManageOnOpen();
    }
  } catch (e) {
    appendLine(e.message || 'Collection failed.', 'error');
    state.managePhase = 'tour_balance';
  } finally {
    finishSaving();
  }
}

function startDeleteFlow() {
  if (!state.lead) return;
  if (!canDeleteCurrentLead()) {
    appendLine('You cannot delete this inquiry.', 'error');
    return;
  }
  state.managePhase = 'delete_confirm';
  const label = state.lead.name || state.lead.package_name || `#${state.lead.id}`;
  appendLine(`Delete "${label}"? Moves to recycle bin. (Y/N)`, 'error');
  refreshGuiIfEnabled();
}

async function submitLeadDelete() {
  if (!state.lead?.id || state.saving) return;
  state.saving = true;
  appendLine('Deleting…', 'dim');
  try {
    await api(`/api/leads/${state.lead.id}`, { method: 'DELETE' });
    appendLine('Inquiry deleted (recycle bin).', 'success');
    state.mode = 'menu';
    clearManageState();
    welcomeMessage();
  } catch (e) {
    appendLine(e.message || 'Delete failed.', 'error');
    state.managePhase = 'questions';
  } finally {
    finishSaving();
  }
}

async function openLeadForManage(id) {
  closeAssistantSearchPopup();
  appendLine('Loading…', 'dim');
  try {
    const res = await api(`/api/leads/${id}`);
    const lead = res.lead || res;
    state.lead = lead;
    state.leadAccess = res.access || null;
    state.mode = 'manage';
    state.managePhase = 'questions';
    state.stageAnswers = {};
    state.manageStep = 0;
    state.searchResults = [];
    state.duePayScheduled = false;
    appendHtml(formatLeadSummary(lead, state.leadAccess), 'summary');
    appendLine(`Stage: ${statusDisplayName(lead.status)}`, 'system');
    const hints = [];
    if (!state.leadAccess?.can_edit) {
      hints.push('View only — type request edit to ask the owner.');
    }
    if (canDeleteCurrentLead()) hints.push('Type delete to remove.');
    if (tourHasBalanceDue()) {
      hints.push(`Type collect balance (${fmtAmt(leadOutstandingBalance(lead))} due).`);
    }
    if (hints.length) appendLine(hints.join(' '), 'dim');
    routeManageOnOpen();
    refreshGuiIfEnabled();
  } catch (e) {
    appendLine(e.message || 'Load failed.', 'error');
    state.mode = 'menu';
    clearManageState();
  }
}

function currentManageSteps() {
  if (!state.lead) return [];
  if (state.stageStepList) return state.stageStepList;
  return activeStageSteps(state.lead.type, workingStatus(), state.lead, state.stageAnswers);
}

function appendStageSummary() {
  if (!state.lead) return;
  const type = state.lead.type || 'ticket';
  const status = state.lead.status;
  const sd = state.lead.extra?.stage_data?.[status] || {};
  const text = formatStageHistoryDescription(type, status, sd, state.lead);
  if (!text) {
    appendLine('No saved details for this stage yet.', 'dim');
    return;
  }
  text.split('\n').filter(Boolean).forEach((line) => appendLine(line, 'summary'));
}

function showInquiryOptionsMenu({ finished = false } = {}) {
  state.managePhase = 'inquiry_options';
  state.stageStepList = null;
  state.stageAnswers = {};
  state.manageStep = 0;
  state.pendingStatus = null;
  state.missingOnly = false;
  if (finished) {
    appendLine('Inquiry process COMPLETE.', 'success');
    appendLine(`Final stage: ${statusDisplayName(state.lead.status)}`, 'system');
  } else {
    appendLine(`Current stage: ${statusDisplayName(state.lead.status)}`, 'system');
  }
  appendStageSummary();
  appendLine('What would you like to do with this inquiry?', 'system');
  const opts = ['summary', 'history', 'edit', 'pricing', 'call'];
  if (tourHasBalanceDue()) opts.push('collect balance');
  opts.push('ig menu');
  appendLine(`  ${opts.join(' · ')}`, 'dim');
  refreshGuiIfEnabled();
}

function showCompleteMenu() {
  showInquiryOptionsMenu({ finished: true });
}

function showStageReview() {
  if (!state.lead) return;
  const type = state.lead.type || 'ticket';
  const status = state.lead.status;
  const next = getNextStatus(type, status);

  if (isCompletedStatus(type, status) || !next) {
    showCompleteMenu();
    return;
  }

  state.managePhase = 'stage_review';
  state.stageStepList = null;
  state.stageAnswers = {};
  state.manageStep = 0;
  state.pendingStatus = null;
  state.missingOnly = false;
  appendLine(`Current stage: ${statusDisplayName(status)} — all details saved.`, 'success');
  appendStageSummary();
  appendHtml(`Ready to move to <strong>${esc(statusDisplayName(next))}</strong>?`, 'system');
  appendLine('  Y — proceed to next stage', 'dim');
  appendLine('  N — stay on this stage', 'dim');
  appendLine('  edit — change this stage\'s details', 'dim');
  refreshGuiIfEnabled();
}

function routeManageAfterStatusChange() {
  if (!state.lead) return;
  const type = state.lead.type || 'ticket';
  const status = state.lead.status;
  if (isCompletedStatus(type, status)) {
    showCompleteMenu();
    return;
  }
  const missing = missingStageSteps(type, status, state.lead, {});
  if (missing.length) {
    appendLine(`Fill in ${statusDisplayName(status)} to continue.`, 'system');
    if (type === 'tour' && (status === 'quotation' || status === 'tour_accepted_advance')) {
      startStageQuestions({ editAll: true });
    } else {
      startStageQuestions({ fullList: missing, missingOnly: true });
    }
    return;
  }
  showStageReview();
}

function routeManageOnOpen() {
  if (!state.lead) return;
  const type = state.lead.type || 'ticket';
  const status = state.lead.status;

  state.pendingStatus = null;
  state.stageAnswers = {};
  state.manageStep = 0;
  state.stageStepList = null;
  state.missingOnly = false;

  if (isClosedStatus(status)) {
    showInquiryOptionsMenu({ finished: true });
    appendLine(`Closed as: ${statusDisplayName(status)}`, 'dim');
    return;
  }

  if (isCompletedStatus(type, status)) {
    showCompleteMenu();
    return;
  }

  const allSteps = activeStageSteps(type, status, state.lead, {});
  if (!allSteps.length) {
    showStageReview();
    return;
  }

  const missing = missingStageSteps(type, status, state.lead, {});
  if (missing.length) {
    appendLine(`Current stage: ${statusDisplayName(status)}`, 'system');
    appendLine(`${missing.length} detail${missing.length === 1 ? '' : 's'} still needed for this stage.`, 'dim');
    if (type === 'tour' && (status === 'quotation' || status === 'tour_accepted_advance')) {
      startStageQuestions({ editAll: true });
    } else {
      startStageQuestions({ fullList: missing, missingOnly: true });
    }
    return;
  }

  showStageReview();
}

function startStageQuestions({ fullList = null, missingOnly = false, editAll = false } = {}) {
  const type = state.lead?.type || 'ticket';
  const status = workingStatus();
  state.managePhase = 'questions';
  state.stageAnswers = {};
  state.manageStep = 0;
  state.missingOnly = !!missingOnly;

  if (fullList) {
    state.stageStepList = fullList;
  } else if (editAll || !missingOnly || (type === 'tour' && (status === 'quotation' || status === 'tour_accepted_advance'))) {
    state.stageStepList = activeStageSteps(type, status, state.lead, {});
  } else {
    state.stageStepList = missingStageSteps(type, status, state.lead, {});
  }

  if (!state.stageStepList.length) {
    state.stageStepList = null;
    if (state.pendingStatus) {
      saveStatusOnly(state.pendingStatus);
      return;
    }
    showStageReview();
    return;
  }

  const prefix = state.pendingStatus ? 'Complete' : (missingOnly ? 'Still needed' : 'Update');
  appendLine(`${prefix} — ${statusDisplayName(status)}.`);
  askManageStep();
  refreshGuiIfEnabled();
}

function beginManageQuestions() {
  startStageQuestions({ missingOnly: !state.pendingStatus });
}

function currentManageStep() {
  return currentManageSteps()[state.manageStep] || null;
}

function refreshDynamicStageStepList(answeredKey) {
  const type = state.lead?.type;
  const status = workingStatus();
  if (type === 'tour' && ['quotation', 'tour_accepted_advance'].includes(status)) {
    state.stageStepList = activeStageSteps(type, status, state.lead, state.stageAnswers);
    if (answeredKey) {
      const prevIdx = state.stageStepList.findIndex((s) => s.key === answeredKey);
      state.manageStep = prevIdx >= 0 ? prevIdx + 1 : state.manageStep + 1;
    } else {
      state.manageStep += 1;
    }
    if (state.manageStep > state.stageStepList.length) {
      state.manageStep = state.stageStepList.length;
    }
    return;
  }
  state.manageStep += 1;
}

function stageAnswerMissing(step, answers) {
  const val = answers[step.key];
  if (val == null || val === '') return true;
  if (typeof val === 'number' && !Number.isFinite(val)) return true;
  return false;
}

function askManageStep() {
  const step = currentManageStep();
  if (!step) {
    finishManageQuestions();
    return;
  }
  const q = typeof step.ask === 'function' ? step.ask(state.stageAnswers, state.lead) : step.ask;
  appendLine(q);
  const def = typeof step.default === 'function' ? step.default(state.stageAnswers, state.lead) : step.default;
  if (def != null && def !== '') {
    appendLine(`Saved: ${def} — enter to keep.`, 'dim');
  } else if (step.hint) {
    appendLine(step.hint, 'dim');
  }
  refreshGuiIfEnabled();
}

async function saveStatusOnly(status) {
  state.saving = true;
  try {
    await api(`/api/leads/${state.lead.id}/status`, { method: 'PATCH', body: { status } });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    state.pendingStatus = null;
    appendLine(`Now at: ${statusDisplayName(state.lead.status)}`, 'success');
    routeManageAfterStatusChange();
  } catch (e) {
    appendLine(e.message || 'Status change failed.', 'error');
    state.pendingStatus = null;
  } finally {
    finishSaving();
  }
}

async function finishManageQuestions() {
  let status = workingStatus();
  const leadType = state.lead.type || 'ticket';
  const allSteps = activeStageSteps(leadType, status, state.lead, state.stageAnswers);
  applyUnansweredStepDefaults(allSteps, state.stageAnswers, state.lead);
  const err = validateStageAnswers(leadType, status, state.lead, state.stageAnswers);
  if (err) {
    appendLine(err, 'error');
    const missingIdx = allSteps.findIndex((st) => {
      if (st.optional || String(st.key || '').startsWith('_')) return false;
      if (typeof st.isSaved === 'function' && st.isSaved(state.lead)) return false;
      return stageAnswerMissing(st, state.stageAnswers);
    });
    if (missingIdx >= 0) {
      state.stageStepList = allSteps;
      state.manageStep = missingIdx;
      askManageStep();
    } else {
      appendLine('Type save to retry, or edit to re-enter this stage.', 'dim');
    }
    return;
  }
  state.saving = true;
  const advancing = state.pendingStatus && state.pendingStatus !== state.lead.status;
  appendLine(advancing ? 'Saving & advancing…' : 'Saving…', 'dim');
  try {
    const body = buildStatusPatchBody(leadType, status, state.lead, state.stageAnswers);
    if (body.status && body.status !== status) status = body.status;
    await api(`/api/leads/${state.lead.id}/status`, { method: 'PATCH', body });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    state.pendingStatus = null;
    state.stageStepList = null;
    state.missingOnly = false;
    if (advancing) {
      appendLine(`Now at: ${statusDisplayName(state.lead.status)}`, 'success');
      routeManageAfterStatusChange();
    } else {
      appendLine('Saved.', 'success');
      showStageReview();
    }
  } catch (e) {
    appendLine(e.message || 'Save failed.', 'error');
    state.stageAnswers = {};
    state.manageStep = 0;
    appendLine('Please re-enter stage details, or type ig.', 'dim');
    startStageQuestions({ editAll: true });
  } finally {
    finishSaving();
  }
}

function offerAdvance() {
  const leadType = state.lead?.type || 'ticket';
  const next = getNextStatus(leadType, state.lead?.status);
  state.managePhase = 'advance';
  if (!next) {
    showCompleteMenu();
    return;
  }
  if (leadType === 'ticket' && next === 'ticket_issued' && leadHasDueBalance(state.lead) && !state.duePayScheduled) {
    const due = leadOutstandingBalance(state.lead);
    appendHtml(`Cannot issue ticket — <span class="due-text">balance due ${esc(fmtAmt(due))}</span>`, 'error');
    appendLine('Enter amount collected (LKR), or pay date YYMMDD when client will settle.', 'dim');
    state.managePhase = 'due_block';
    return;
  }
  if (leadType === 'tour' && tourHasBalanceDue()) {
    appendHtml(`Balance due: <span class="due-text">${esc(fmtAmt(leadOutstandingBalance(state.lead)))}</span>`, 'error');
    appendLine('Type collect balance to record payment before continuing.', 'dim');
  }
  appendHtml(`Move to <strong>${esc(statusDisplayName(next))}</strong>? (Y/N)`, 'system');
}

async function recordDuePayment(amount) {
  const due = leadOutstandingBalance(state.lead);
  const pay = Math.min(Math.round(amount), due);
  if (pay <= 0) {
    appendLine('Enter a valid amount.', 'error');
    return;
  }
  state.saving = true;
  try {
    const body = buildStatusPatchBody('ticket', 'booking_confirmed', state.lead, {
      paid_on: todayISO(),
      payment_amount: pay,
    });
    body.status = state.lead.status;
    await api(`/api/leads/${state.lead.id}/status`, { method: 'PATCH', body });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    const remain = leadOutstandingBalance(state.lead);
    appendLine(`Payment recorded: ${fmtAmt(pay)}`, 'success');
    if (remain > 0) {
      appendHtml(`Remaining: <span class="due-text">${esc(fmtAmt(remain))}</span>`, 'error');
      state.managePhase = 'due_block';
      appendLine('Collect more, or enter pay date YYMMDD to proceed with ticket issue.', 'dim');
    } else {
      state.duePayScheduled = false;
      offerAdvance();
    }
  } catch (e) {
    appendLine(e.message || 'Payment failed.', 'error');
  } finally {
    finishSaving();
  }
}

async function recordDuePayDate(raw) {
  const date = parseSmartDate(raw);
  if (!date) {
    appendLine('Invalid date. Use YYMMDD.', 'error');
    return;
  }
  state.saving = true;
  try {
    const extra = { ...(state.lead.extra || {}), remain_pay_by: `${date}T17:00` };
    await api(`/api/leads/${state.lead.id}`, {
      method: 'PUT',
      body: buildLeadUpdateBody(state.lead, { extra }, state.agent.id),
    });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    state.duePayScheduled = true;
    appendLine(`Pay-by date recorded: ${fmtDate(date)}`, 'success');
    offerAdvance();
  } catch (e) {
    appendLine(e.message || 'Could not save pay date.', 'error');
  } finally {
    finishSaving();
  }
}

async function requestEditAccess() {
  if (!state.lead?.id) return;
  try {
    await api(`/api/leads/${state.lead.id}/request-edit`, { method: 'POST', body: {} });
    appendLine('Edit access requested from the inquiry owner.', 'success');
  } catch (e) {
    appendLine(e.message || 'Request failed.', 'error');
  }
}

function startEditFlow() {
  if (!state.leadAccess?.can_edit) {
    appendLine('You cannot edit this inquiry. Type request edit to ask the owner.', 'error');
    return;
  }
  state.managePhase = 'edit_menu';
  state.editField = null;
  state.editPaxTarget = null;
  state.editPaxField = null;
  state.notInterestedStep = 0;
  appendLine(editMenuText(state.lead), 'dim');
  refreshGuiIfEnabled();
}

function currentNotInterestedSteps() {
  return notInterestedSteps(state.lead?.type || 'ticket');
}

function currentNotInterestedStep() {
  return currentNotInterestedSteps()[state.notInterestedStep] || null;
}

function askNotInterestedStep() {
  const step = currentNotInterestedStep();
  if (!step) {
    submitNotInterestedStatus();
    return;
  }
  const q = typeof step.ask === 'function' ? step.ask(state.stageAnswers, state.lead) : step.ask;
  appendLine(q);
  if (step.hint) appendLine(step.hint, 'dim');
  refreshGuiIfEnabled();
}

function startNotInterestedFlow() {
  if (isClosedStatus(state.lead?.status)) {
    appendLine('This inquiry is already closed.', 'error');
    return;
  }
  state.managePhase = 'edit_not_interested';
  state.notInterestedStep = 0;
  state.stageAnswers = {};
  appendLine('Mark as client not interested.');
  askNotInterestedStep();
}

async function submitNotInterestedStatus() {
  const leadType = state.lead?.type || 'ticket';
  const err = validateNotInterestedAnswers(leadType, state.stageAnswers);
  if (err) {
    appendLine(err, 'error');
    state.notInterestedStep = 0;
    state.stageAnswers = {};
    askNotInterestedStep();
    return;
  }
  state.saving = true;
  appendLine('Updating status…', 'dim');
  try {
    const body = buildNotInterestedPatchBody(leadType, state.stageAnswers);
    await api(`/api/leads/${state.lead.id}/status`, { method: 'PATCH', body });
    const refreshed = await api(`/api/leads/${state.lead.id}`);
    state.lead = refreshed.lead || refreshed;
    appendLine(`Marked: ${statusDisplayName('client_not_interested')}`, 'success');
    appendHtml(formatLeadSummary(state.lead, state.leadAccess), 'summary');
    appendLine('Inquiry closed.', 'dim');
    state.mode = 'menu';
    clearManageState();
    welcomeMessage();
  } catch (e) {
    appendLine(e.message || 'Status change failed.', 'error');
    state.notInterestedStep = 0;
    state.stageAnswers = {};
    askNotInterestedStep();
  } finally {
    finishSaving();
  }
}

async function showInquiryHistory() {
  if (!state.lead?.id) {
    appendLine('No inquiry open.', 'error');
    return;
  }
  appendLine('Loading history…', 'dim');
  try {
    const data = await api(`/api/leads/${state.lead.id}`);
    const items = buildHistoryItems({
      lead: data.lead || state.lead,
      notes: data.notes || [],
      call_logs: data.call_logs || [],
      audit_events: data.audit_events || [],
    });
    if (!items.length) {
      appendLine('No history yet.', 'dim');
      refreshGuiIfEnabled();
      return;
    }
    appendLine(
      `Inquiry history (${items.length} ${items.length === 1 ? 'entry' : 'entries'})`,
      'history-head',
    );
    items.forEach((item, i) => {
      appendLine(`${formatHistoryTime(item.time)} · ${item.title}`, 'history-meta');
      appendLine(`  ${item.who || 'Agent'}`, 'history-who');
      if (item.text) {
        item.text.split('\n').forEach((ln) => {
          if (ln.trim()) appendLine(`  ${ln.trim()}`, 'history-body');
        });
      }
      if (i < items.length - 1) appendLine('', 'history-gap');
    });
    refreshGuiIfEnabled();
  } catch (e) {
    appendLine(e.message || 'Could not load history.', 'error');
  }
}

function currentCallLogSteps() {
  return callLogSteps();
}

function currentCallLogStep() {
  return currentCallLogSteps()[state.callLogStep] || null;
}

function askCallLogStep() {
  const step = currentCallLogStep();
  if (!step) {
    submitCallLog();
    return;
  }
  const q = typeof step.ask === 'function' ? step.ask(state.callLogAnswers, state.lead) : step.ask;
  appendLine(q);
  const def = typeof step.default === 'function' ? step.default(state.callLogAnswers, state.lead) : step.default;
  if (def != null && def !== '') {
    appendLine(`Current: ${def} — enter to keep.`, 'dim');
  } else if (step.hint) {
    appendLine(step.hint, 'dim');
  }
  refreshGuiIfEnabled();
}

function resumeAfterCallLog() {
  const resume = state.callLogResume;
  state.callLogResume = null;
  state.callLogStep = 0;
  state.callLogAnswers = {};
  state.managePhase = 'questions';
  if (resume) {
    state.manageStep = resume.manageStep;
    state.stageAnswers = resume.stageAnswers;
    if (currentManageStep()) {
      askManageStep();
      return;
    }
  }
  routeManageOnOpen();
}

function startCallLogFlow() {
  if (isViewer(state.agent)) {
    appendLine('View-only accounts cannot log calls.', 'error');
    return;
  }
  if (state.managePhase === 'call_log') return;
  if (state.managePhase === 'questions') {
    state.callLogResume = {
      manageStep: state.manageStep,
      stageAnswers: { ...state.stageAnswers },
    };
  } else {
    state.callLogResume = null;
  }
  state.managePhase = 'call_log';
  state.callLogStep = 0;
  state.callLogAnswers = {};
  appendLine('Log call.');
  askCallLogStep();
}

async function submitCallLog() {
  const err = validateCallLogAnswers(state.callLogAnswers);
  if (err) {
    appendLine(err, 'error');
    state.callLogStep = 0;
    state.callLogAnswers = {};
    askCallLogStep();
    return;
  }
  state.saving = true;
  appendLine('Saving call…', 'dim');
  try {
    const body = buildCallLogBody(state.callLogAnswers);
    await api(`/api/leads/${state.lead.id}/calls`, { method: 'POST', body });
    appendLine('Call logged.', 'success');
    resumeAfterCallLog();
  } catch (e) {
    appendLine(e.message || 'Could not save call.', 'error');
  } finally {
    finishSaving();
  }
}

async function saveLeadEdit(changes, { resumePhase, noteText } = {}) {
  if (!state.lead?.id) {
    appendLine('No inquiry loaded to update.', 'error');
    return;
  }
  state.saving = true;
  try {
    const body = buildLeadUpdateBody(state.lead, changes, state.agent.id);
    if (noteText) body.note_text = noteText;
    await api(`/api/leads/${state.lead.id}`, { method: 'PUT', body });
    try {
      const refreshed = await api(`/api/leads/${state.lead.id}`);
      state.lead = refreshed.lead || refreshed;
    } catch {
      if (changes.name) state.lead = { ...state.lead, name: changes.name };
      if (changes.phone) state.lead = { ...state.lead, phone: changes.phone };
      if (changes.pnr !== undefined) state.lead = { ...state.lead, pnr: changes.pnr };
      if (changes.source) state.lead = { ...state.lead, source: changes.source };
      if (changes.airline !== undefined) state.lead = { ...state.lead, airline: changes.airline };
      if (changes.package_name !== undefined) state.lead = { ...state.lead, package_name: changes.package_name };
      if (changes.custom_req !== undefined) state.lead = { ...state.lead, custom_req: changes.custom_req };
      if (changes.is_custom !== undefined) state.lead = { ...state.lead, is_custom: changes.is_custom };
      if (changes.extra) state.lead = { ...state.lead, extra: { ...(state.lead.extra || {}), ...changes.extra } };
    }
    appendLine(noteText ? 'Pricing updated.' : 'Updated.', 'success');
    if (!noteText) appendHtml(formatLeadSummary(state.lead, state.leadAccess), 'summary');
    state.managePhase = resumePhase || 'questions';
    if (resumePhase === 'pricing_menu') {
      state.pricingTarget = null;
      state.pricingField = null;
    } else if (resumePhase === 'edit_pax_field_menu') {
      state.editPaxField = null;
    } else if (resumePhase === 'edit_pax_pick') {
      state.editField = null;
      state.editPaxTarget = null;
      state.editPaxField = null;
    } else {
      state.editField = null;
      state.editPaxTarget = null;
      state.editPaxField = null;
    }
  } catch (e) {
    appendLine(e.message || 'Update failed.', 'error');
  } finally {
    finishSaving();
  }
}

function resumeAfterPricing() {
  const resume = state.pricingResume;
  state.pricingResume = null;
  state.pricingTarget = null;
  state.pricingField = null;
  state.managePhase = 'questions';
  if (resume) {
    state.manageStep = resume.manageStep;
    state.stageAnswers = resume.stageAnswers;
    if (currentManageStep()) {
      askManageStep();
      return;
    }
  }
  appendLine('Type history · pricing · call · edit · summary · or continue.', 'dim');
  refreshGuiIfEnabled();
}

function startPricingFlow() {
  if (!state.lead?.id) {
    appendLine('No inquiry open.', 'error');
    return;
  }
  const editable = !!state.leadAccess?.can_edit;
  appendLine(formatPricingSummaryText(state.lead), 'dim');
  if (!editable) {
    appendLine('View only — type request edit to change pricing.', 'dim');
    return;
  }
  if (state.managePhase === 'questions') {
    state.pricingResume = {
      manageStep: state.manageStep,
      stageAnswers: { ...state.stageAnswers },
    };
  } else {
    state.pricingResume = null;
  }
  state.managePhase = 'pricing_menu';
  state.pricingTarget = null;
  state.pricingField = null;
  appendLine(pricingMenuText(state.lead, { editable: true }), 'dim');
  refreshGuiIfEnabled();
}

function returnToPricingMenu() {
  state.managePhase = 'pricing_menu';
  state.pricingTarget = null;
  state.pricingField = null;
  appendLine(formatPricingSummaryText(state.lead), 'dim');
  appendLine(pricingMenuText(state.lead, { editable: true }), 'dim');
  refreshGuiIfEnabled();
}

async function submitPricingValue(text) {
  const { pricingTarget: target, pricingField: fieldId } = state;
  if (!target) return;

  const err = validatePricingValue(target, fieldId, text);
  if (err) {
    appendLine(err, 'error');
    return;
  }

  const value = parsePricingValue(text);
  const beforeSnap = capturePricingSnapshot(state.lead);
  const oldValue = target.kind === 'simple'
    ? (target.fieldKey === 'amount'
      ? (state.lead.amount ?? state.lead.quote_amount)
      : state.lead.cost_amount)
    : (fieldId === 'cost' ? target.cost : target.profit);

  const fieldChanges = buildPricingFieldChanges(state.lead, target, fieldId, value);
  const changes = {
    ...fieldChanges,
    recalcPricing: !!fieldChanges.recalcPricing,
  };
  const preview = previewLeadAfterPricingChanges(state.lead, changes);
  const afterSnap = capturePricingSnapshot(preview);
  const noteText = buildPricingHistoryNote(beforeSnap, afterSnap, {
    target,
    fieldId: target.kind === 'simple' ? target.fieldKey : fieldId,
    oldValue,
    newValue: value,
  });

  state.pricingTarget = null;
  state.pricingField = null;
  await saveLeadEdit(changes, { resumePhase: 'pricing_menu', noteText });
  if (state.managePhase !== 'pricing_menu') return;
  appendLine(formatPricingSummaryText(state.lead), 'dim');
  appendLine(pricingMenuText(state.lead, { editable: true }), 'dim');
}

async function applyEditField(text) {
  const err = validateSimpleField(state.editField, text);
  if (err) {
    appendLine(err, 'error');
    return;
  }
  const changes = parseSimpleField(state.editField, text, {
    matchSource,
    normalizePhone,
    matchPackage: (v) => matchPackage(v, state.lead?.type),
  });
  await saveLeadEdit(changes);
}

async function submitPassengerRemoval() {
  const target = state.editPaxTarget;
  if (!target) return;
  const changes = buildPassengerRemovalChanges(state.lead, target);
  state.editPaxTarget = null;
  await saveLeadEdit(changes, { resumePhase: 'edit_pax_pick' });
  if (state.managePhase !== 'edit_pax_pick') return;
  if (listPassengerTargets(state.lead).length) {
    appendLine(passengerPickMenuText(state.lead), 'dim');
  } else {
    state.managePhase = 'edit_menu';
    appendLine('No passengers left to edit.', 'dim');
    appendLine(editMenuText(state.lead), 'dim');
  }
}

async function submitPassengerFieldEdit(text) {
  const { editPaxTarget: target, editPaxField: fieldId } = state;
  if (!target || !fieldId) return;

  const err = validatePassengerFieldValue(target, fieldId, text, state.lead);
  if (err) {
    appendLine(err, 'error');
    return;
  }
  const value = parsePassengerFieldValue(target, fieldId, text);
  const changes = buildPassengerFieldChanges(state.lead, target, fieldId, value);
  await saveLeadEdit(changes, { resumePhase: 'edit_pax_field_menu' });
  if (state.managePhase === 'edit_pax_field_menu' && state.editPaxTarget) {
    appendLine(passengerFieldMenuText(state.editPaxTarget, state.lead), 'dim');
  }
}

function advanceLeadStatus() {
  const leadType = state.lead?.type || 'ticket';
  const next = getNextStatus(leadType, state.lead.status);
  if (!next) {
    showCompleteMenu();
    return;
  }
  state.pendingStatus = next;
  if (leadType === 'tour' && (next === 'quotation' || next === 'tour_accepted_advance')) {
    startStageQuestions({ editAll: true });
  } else {
    startStageQuestions({ missingOnly: true });
  }
}

function startCaptureFlow(captureType) {
  clearManageState();
  state.captureType = captureType;
  state.mode = 'ticket';
  state.step = 0;
  if (captureType === 'visa') state.data = initVisaData(ticketHelpers());
  else if (captureType === 'tour') state.data = initTourData(ticketHelpers());
  else state.data = initNewTicketData();
  const labels = { ticket: 'ticket', visa: 'visa', tour: 'tour package' };
  appendLine(`New ${labels[captureType] || captureType} inquiry.`);
  appendLine(`Dates: ${DATE_HINT} · Times: HHMM (max 23:59) · Confirm with Y/N where asked.`, 'dim');
  askNewTicketStep();
}

function startTicketFlow() {
  startCaptureFlow('ticket');
}

function startVisaFlow() {
  startCaptureFlow('visa');
}

function startTourFlow() {
  startCaptureFlow('tour');
}

function askNewTicketStep() {
  const step = currentNewTicketStep();
  if (!step) {
    state.mode = 'confirm';
    appendLine('Review summary:');
    showSummary();
    appendLine('Save this inquiry? (Y/N)', 'dim');
    refreshGuiIfEnabled();
    return;
  }
  const q = typeof step.ask === 'function' ? step.ask(state.data) : step.ask;
  appendLine(q);
  if (step.hint) appendLine(step.hint, 'dim');
  refreshGuiIfEnabled();
}

function handleMenuInput(text) {
  const raw = text.trim();
  const s = raw.toLowerCase();
  if (s === 'help' || s === '?') {
    helpMessage();
    return;
  }
  if (s === 'clear') {
    clearTranscript();
    welcomeMessage();
    return;
  }
  if (s === 'stats' || s === 'status') {
    showAgentStats();
    return;
  }
  const themeMatch = s.match(/^theme\s+(\S+)$/);
  if (themeMatch) {
    const mode = parseThemeCommand(themeMatch[1]);
    if (!mode) {
      appendLine('Themes: theme sky · theme white · theme vibrant · theme green', 'dim');
      return;
    }
    setAssistantTheme(mode);
    return;
  }
  if (/^(new ticket|new ticket inquiry|ticket|record ticket|air ticket)$/.test(s)) {
    startTicketFlow();
    return;
  }
  if (/^(new visa|visa inquiry|record visa)$/.test(s)) {
    startVisaFlow();
    return;
  }
  if (/^(new tour|tour inquiry|record tour|new package)$/.test(s)) {
    startTourFlow();
    return;
  }
  if (isIgCommand(raw)) {
    resetSession();
    return;
  }
  if (raw.startsWith('/')) {
    promptSearchMode(raw.trim());
    return;
  }
  const searchQuery = extractSearchQuery(raw);
  if (searchQuery) {
    promptSearchMode(searchQuery);
    return;
  }
  if (s === 'summary') {
    appendLine('No inquiry in progress.', 'dim');
    return;
  }
  if (isIgCommand(text)) {
    resetSession();
    return;
  }
  const knowledge = tryKnowledgeAnswer(raw, { visaCosts: state.visaCosts });
  if (knowledge) {
    knowledge.forEach((line) => appendLine(line, 'system'));
    return;
  }
  appendLine('Type help for commands · new ticket · /text john · /mine · ig menu.', 'dim');
}

function handleSearchInput(text) {
  const raw = text.trim();
  const s = raw.toLowerCase();
  if (isIgCommand(text)) {
    resetSession();
    return;
  }

  if (s === 'md' || s === 'md-' || s === 'next' || s === 'next page') {
    if (state.searchPage < state.searchTotalPages) {
      showSearchPage(state.searchPage + 1, { heading: false });
    } else {
      appendLine('Already on the last page.', 'dim');
    }
    return;
  }

  if (s === 'mu' || s === 'mu-' || s === 'prev' || s === 'previous' || s === 'previous page') {
    if (state.searchPage > 1) {
      showSearchPage(state.searchPage - 1, { heading: false });
    } else {
      appendLine('Already on the first page.', 'dim');
    }
    return;
  }

  const openMatch = s.match(/^open(?:\s+(\d+))?$/);
  if (openMatch) {
    const pick = openMatch[1]
      ? Number(openMatch[1]) - 1
      : state.searchPick;
    if (Number.isInteger(pick) && pick >= 0 && pick < state.searchResults.length) {
      openSearchResult(pick);
      return;
    }
    appendLine('Type a number on this page first, or use open 2.', 'dim');
    return;
  }

  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= state.searchResults.length) {
    state.searchPick = num - 1;
    appendLine(`Selected #${num} (page ${state.searchPage}). Type open to manage.`, 'success');
    return;
  }
  appendLine('Type 1–10 then open · MD next · MU prev · ig menu.', 'dim');
}

function handleManageInput(text) {
  const s = text.trim().toLowerCase();
  if (isIgCommand(text)) {
    resetSession();
    return;
  }
  if (s === 'summary' && state.lead) {
    appendHtml(formatLeadSummary(state.lead, state.leadAccess), 'summary');
    return;
  }
  if (s === 'history' || s === 'log history' || s === 'inquiry history') {
    showInquiryHistory();
    return;
  }
  if (s === 'call' || s === 'log call' || s === 'call log') {
    startCallLogFlow();
    return;
  }
  if (s === 'pricing' || s === 'price' || s === 'prices') {
    startPricingFlow();
    return;
  }
  if (s === 'edit') {
    startEditFlow();
    return;
  }
  if (s === 'request edit' || s === 'request') {
    requestEditAccess();
    return;
  }
  if (s === 'delete' || s === 'del' || s === 'delete inquiry') {
    startDeleteFlow();
    return;
  }
  if (s === 'collect balance' || s === 'collect' || s === 'balance') {
    startTourBalanceCollect();
    return;
  }

  if (state.managePhase === 'tour_balance') {
    const amt = Number(text.trim().replace(/,/g, ''));
    if (Number.isFinite(amt) && amt > 0) {
      recordTourBalanceCollection(amt);
      return;
    }
    appendLine('Enter amount collected (LKR).', 'dim');
    return;
  }

  if (state.managePhase === 'delete_confirm') {
    if (isYes(text)) {
      submitLeadDelete();
      return;
    }
    if (isNo(text)) {
      appendLine('Delete cancelled.', 'dim');
      routeManageOnOpen();
      return;
    }
    appendLine('Reply Y to delete or N to cancel.', 'dim');
    return;
  }

  if (state.managePhase === 'due_block') {
    const amt = Number(text.trim().replace(/,/g, ''));
    if (Number.isFinite(amt) && amt > 0) {
      recordDuePayment(amt);
      return;
    }
    if (/^\d{6}$/.test(text.trim()) || /^\d{4}-\d{2}-\d{2}$/.test(text.trim())) {
      recordDuePayDate(text.trim());
      return;
    }
    appendLine('Enter amount collected (LKR) or pay date YYMMDD.', 'dim');
    return;
  }

  if (state.managePhase === 'edit_menu') {
    const choice = parseEditChoice(text, state.lead);
    if (choice === 'done') {
      routeManageOnOpen();
      return;
    }
    if (!choice) {
      appendLine(`Pick 1–${editMenuMaxChoice(state.lead)} or done.`, 'dim');
      return;
    }
    if (choice === 'not_interested') {
      startNotInterestedFlow();
      return;
    }
    if (choice === 'passengers') {
      if (!listPassengerTargets(state.lead).length) {
        appendLine('No per-passenger data on this inquiry.', 'error');
        appendLine(passengerPickMenuText(state.lead), 'dim');
        return;
      }
      state.managePhase = 'edit_pax_pick';
      appendLine(passengerPickMenuText(state.lead), 'dim');
      return;
    }
    state.editField = choice;
    state.managePhase = 'edit_field';
    const { question, hint } = simpleFieldPrompt(choice, state.lead);
    appendLine(question);
    if (hint) appendLine(hint, 'dim');
    return;
  }

  if (state.managePhase === 'edit_pax_pick') {
    const target = parsePassengerPick(text, state.lead);
    if (target === 'back') {
      state.managePhase = 'edit_menu';
      appendLine(editMenuText(state.lead), 'dim');
      return;
    }
    if (!target) {
      appendLine('Pick a passenger number or back.', 'dim');
      return;
    }
    state.editPaxTarget = target;
    state.managePhase = 'edit_pax_field_menu';
    appendLine(passengerFieldMenuText(target, state.lead), 'dim');
    return;
  }

  if (state.managePhase === 'edit_pax_field_menu') {
    const fieldId = parsePassengerFieldPick(text, state.editPaxTarget, state.lead);
    if (fieldId === 'back') {
      state.editPaxTarget = null;
      state.managePhase = 'edit_pax_pick';
      appendLine(passengerPickMenuText(state.lead), 'dim');
      return;
    }
    if (fieldId === '__remove__') {
      state.managePhase = 'edit_pax_remove_confirm';
      appendLine(passengerRemoveConfirmText(state.editPaxTarget, state.lead));
      return;
    }
    if (!fieldId) {
      const removeErr = canRemovePassengerTarget(state.lead, state.editPaxTarget);
      if (removeErr && String(text).trim().toLowerCase() === 'remove') {
        appendLine(removeErr, 'error');
        return;
      }
      appendLine('Pick a field number, remove, or back.', 'dim');
      return;
    }
    state.editPaxField = fieldId;
    state.managePhase = 'edit_pax_field';
    const { question, hint } = passengerFieldPrompt(state.editPaxTarget, fieldId, state.lead);
    appendLine(question);
    if (hint) appendLine(hint, 'dim');
    return;
  }

  if (state.managePhase === 'edit_pax_remove_confirm') {
    if (isYes(text)) {
      submitPassengerRemoval();
      return;
    }
    if (isNo(text)) {
      state.managePhase = 'edit_pax_field_menu';
      appendLine(passengerFieldMenuText(state.editPaxTarget, state.lead), 'dim');
      return;
    }
    appendLine('Reply Y or N.', 'dim');
    return;
  }

  if (state.managePhase === 'pricing_menu') {
    const choice = parsePricingPick(text, state.lead);
    if (choice === 'done') {
      resumeAfterPricing();
      return;
    }
    if (!choice) {
      appendLine('Pick a number or done.', 'dim');
      return;
    }
    state.pricingTarget = choice;
    if (choice.kind === 'simple') {
      state.pricingField = choice.fieldKey;
      state.managePhase = 'pricing_value';
      const { question, hint } = pricingValuePrompt(choice, choice.fieldKey, state.lead);
      appendLine(question);
      if (hint) appendLine(hint, 'dim');
      return;
    }
    state.managePhase = 'pricing_field_menu';
    appendLine(pricingFieldMenuText(choice, state.lead), 'dim');
    return;
  }

  if (state.managePhase === 'pricing_field_menu') {
    const fieldId = parsePricingFieldPick(text, state.pricingTarget);
    if (fieldId === 'back') {
      returnToPricingMenu();
      return;
    }
    if (!fieldId) {
      appendLine('Pick 1 cost · 2 profit · or back.', 'dim');
      return;
    }
    state.pricingField = fieldId;
    state.managePhase = 'pricing_value';
    const { question, hint } = pricingValuePrompt(state.pricingTarget, fieldId, state.lead);
    appendLine(question);
    if (hint) appendLine(hint, 'dim');
    return;
  }

  if (state.managePhase === 'pricing_value') {
    submitPricingValue(text);
    return;
  }

  if (state.managePhase === 'call_log') {
    const step = currentCallLogStep();
    if (!step) {
      submitCallLog();
      return;
    }

    const useDefault = !text.trim() && typeof step.default === 'function';
    const value = useDefault ? String(step.default(state.callLogAnswers, state.lead) ?? '') : text;

    if (step.optional && isSkip(value)) {
      state.callLogAnswers[step.key] = step.parse ? step.parse(value) : null;
      state.callLogStep += 1;
      askCallLogStep();
      return;
    }

    const err = step.validate ? step.validate(value, state.callLogAnswers, state.lead) : null;
    if (err) {
      appendLine(err, 'error');
      return;
    }

    state.callLogAnswers[step.key] = step.parse
      ? step.parse(value, state.callLogAnswers, state.lead)
      : value.trim();
    state.callLogStep += 1;
    askCallLogStep();
    return;
  }

  if (state.managePhase === 'edit_not_interested') {
    const step = currentNotInterestedStep();
    if (!step) {
      submitNotInterestedStatus();
      return;
    }

    const value = text.trim();

    if (step.optional && isSkip(value)) {
      state.stageAnswers[step.key] = step.parse ? step.parse(value) : null;
      state.notInterestedStep += 1;
      askNotInterestedStep();
      return;
    }

    const err = step.validate ? step.validate(value, state.stageAnswers, state.lead) : null;
    if (err) {
      appendLine(err, 'error');
      return;
    }

    state.stageAnswers[step.key] = step.parse ? step.parse(value, state.stageAnswers, state.lead) : value.trim();
    state.notInterestedStep += 1;
    askNotInterestedStep();
    return;
  }

  if (state.managePhase === 'edit_field') {
    applyEditField(text);
    return;
  }

  if (state.managePhase === 'edit_pax_field') {
    submitPassengerFieldEdit(text);
    return;
  }

  if (state.managePhase === 'stage_review') {
    if (isYes(text)) {
      advanceLeadStatus();
      return;
    }
    if (isNo(text)) {
      appendLine('Staying at current stage.', 'dim');
      showInquiryOptionsMenu({ finished: false });
      return;
    }
    if (s === 'edit') {
      startStageQuestions({ editAll: true });
      appendLine('Update stage details — enter keeps saved values.', 'dim');
      return;
    }
    appendLine('Reply Y to proceed, N to stay, or edit to change this stage.', 'dim');
    return;
  }

  if (state.managePhase === 'inquiry_options' || state.managePhase === 'complete_menu') {
    if (s === 'edit') {
      startStageQuestions({ editAll: true });
      appendLine('Update stage details — enter keeps saved values.', 'dim');
      return;
    }
    appendLine('Type summary · history · edit · pricing · call · or ig menu.', 'dim');
    return;
  }

  if (state.managePhase === 'advance') {
    if (isYes(text)) {
      advanceLeadStatus();
      return;
    }
    if (isNo(text)) {
      appendLine('Staying at current stage.', 'dim');
      showStageReview();
      return;
    }
    appendLine('Reply Y or N.', 'dim');
    return;
  }

  const step = currentManageStep();
  if (!step) {
    if (state.managePhase === 'questions') {
      finishManageQuestions();
      return;
    }
    if (s === 'save' || s === 'retry') {
      finishManageQuestions();
    }
    return;
  }

  const useDefault = !text.trim() && typeof step.default === 'function';
  const value = useDefault ? String(step.default(state.stageAnswers, state.lead) ?? '') : text;

  if (step.optional && isSkip(value)) {
    state.stageAnswers[step.key] = step.parse ? step.parse(value) : null;
    refreshDynamicStageStepList(step.key);
    askManageStep();
    return;
  }

  const err = step.validate ? step.validate(value, state.stageAnswers, state.lead) : null;
  if (err) {
    appendLine(err, 'error');
    return;
  }

  state.stageAnswers[step.key] = step.parse ? step.parse(value, state.stageAnswers, state.lead) : value.trim();
  refreshDynamicStageStepList(step.key);
  askManageStep();
  refreshGuiIfEnabled();
}

async function saveTicketInquiry() {
  if (state.saving) return;
  state.saving = true;
  appendLine('Saving…', 'dim');
  try {
    let body;
    if (state.captureType === 'visa') body = buildVisaLeadBody(state.data, state.agent.id, ticketHelpers());
    else if (state.captureType === 'tour') body = buildTourLeadBody(state.data, state.agent.id, ticketHelpers());
    else body = buildNewTicketLeadBody(state.data, state.agent.id, ticketHelpers());
    const res = await api('/api/leads', { method: 'POST', body });
    appendLine('Inquiry saved.', 'success');
    if (res.duplicate_warning) {
      appendLine(`Possible duplicate: ${res.duplicate_warning.name}`, 'dim');
    }
    appendHtml(`Open: <strong>/leads/?open=${esc(res.id)}</strong>`, 'dim');
    state.savedLeadId = res.id;
    state.step = 0;
    state.data = null;
    state.mode = 'post_save';
    appendLine('1. Keep editing  2. Cancel (back to menu)', 'system');
    refreshGuiIfEnabled();
  } catch (e) {
    appendLine(e.message || 'Save failed.', 'error');
  } finally {
    finishSaving();
  }
}

function applyCaptureStep(data, step, text) {
  if (step.optional && isSkip(text)) {
    const val = step.parse ? step.parse(text, data) : null;
    step.apply(val);
    return {};
  }
  const err = step.validate ? step.validate(text, data) : null;
  if (err) return { error: err };
  const val = step.parse ? step.parse(text, data) : text.trim();
  step.apply(val);
  return {};
}

function handleTicketInput(text) {
  if (isIgCommand(text)) {
    resetSession();
    return;
  }
  if (text.trim().toLowerCase() === 'summary') {
    showSummary();
    return;
  }

  const step = currentNewTicketStep();
  if (!step) return;

  if (state.captureType === 'ticket') {
    if (step.optional && isSkip(text)) {
      let val = step.parse ? step.parse(text, state.data) : null;
      if (/^pax_(stu|chd|inf)$/.test(step.key) && (val == null || val === '')) val = 0;
      step.apply(val);
      state.step += 1;
      askNewTicketStep();
      return;
    }
    const err = step.validate ? step.validate(text, state.data) : null;
    if (err) {
      appendLine(err, 'error');
      return;
    }
    const result = applyNewTicketStep(state.data, step, text, ticketHelpers());
    if (result?.error) {
      appendLine(result.error, 'error');
      if (step.key.includes(':fare_confirm')) {
        const costKey = step.key.replace(':fare_confirm', ':cost');
        const idx = ticketFlowSteps().findIndex((st) => st.key === costKey);
        if (idx >= 0) state.step = idx;
      } else if (step.key.startsWith('shared:') && step.key.endsWith(':confirm')) {
        const costKey = step.key.replace(':confirm', ':cost');
        const idx = ticketFlowSteps().findIndex((st) => st.key === costKey);
        if (idx >= 0) state.step = idx;
      }
      askNewTicketStep();
      return;
    }
  } else {
    const result = applyCaptureStep(state.data, step, text);
    if (result?.error) {
      appendLine(result.error, 'error');
      return;
    }
  }

  if (state.data._jumpToKey) {
    const key = state.data._jumpToKey;
    delete state.data._jumpToKey;
    const idx = ticketFlowSteps().findIndex((st) => st.key === key);
    if (idx >= 0) {
      state.step = idx;
      askNewTicketStep();
      return;
    }
  }

  state.step += 1;
  askNewTicketStep();
  refreshGuiIfEnabled();
}

function handleConfirmInput(text) {
  if (isIgCommand(text)) {
    resetSession();
    return;
  }
  if (text.trim().toLowerCase() === 'summary') {
    showSummary();
    return;
  }
  if (isYes(text)) {
    saveTicketInquiry();
    return;
  }
  if (isNo(text)) {
    const labels = { ticket: 'ticket', visa: 'visa', tour: 'tour' };
    appendLine(`Not saved. Restarting ${labels[state.captureType] || 'inquiry'} capture.`, 'dim');
    startCaptureFlow(state.captureType);
    return;
  }
  appendLine('Reply Y to save or N to restart.', 'dim');
}

async function handlePostSaveInput(text) {
  if (isIgCommand(text)) {
    resetSession();
    return;
  }
  const s = text.trim().toLowerCase();
  if (s === '1' || s === 'keep' || s === 'keep editing' || s === 'edit') {
    const id = state.savedLeadId;
    state.savedLeadId = null;
    if (id) await openLeadForManage(id);
    else {
      state.mode = 'menu';
      welcomeMessage();
    }
    return;
  }
  if (s === '2' || s === 'cancel' || isNo(text)) {
    state.savedLeadId = null;
    state.captureType = 'ticket';
    state.mode = 'menu';
    welcomeMessage();
    return;
  }
  appendLine('Reply 1 to keep editing or 2 to cancel.', 'dim');
}

function dispatchInput(text, { fromGui = false, echoUser = true } = {}) {
  if (state.saving) return;

  const trimmed = String(text ?? '').trim();
  const lower = trimmed.toLowerCase();
  const guiCmd = parseGuiCommand(lower);
  if (guiCmd) {
    setGuiEnabled(guiCmd === 'on');
    return;
  }
  const splitCmd = parseSplitCommand(lower);
  if (splitCmd) {
    if (splitCmd === 'on') setSplitOpen(true);
    else if (splitCmd === 'off') setSplitOpen(false);
    else toggleSplit();
    appendLine(isSplitOpen() ? 'Split screen on — Leads & Web on the right.' : 'Split screen off.', 'dim');
    return;
  }

  if (echoUser) appendLine(trimmed || '(enter)', fromGui ? 'user gui' : 'user');

  if (state.mode === 'menu') handleMenuInput(text);
  else if (state.mode === 'search_choice') handleSearchChoiceInput(text);
  else if (state.mode === 'ticket') handleTicketInput(text);
  else if (state.mode === 'confirm') handleConfirmInput(text);
  else if (state.mode === 'post_save') handlePostSaveInput(text);
  else if (state.mode === 'search') handleSearchInput(text);
  else if (state.mode === 'manage') handleManageInput(text);

  refreshGuiIfEnabled();
}

function canSubmitEmpty() {
  if (state.mode === 'manage') return true;
  if (state.mode === 'ticket') {
    const step = currentNewTicketStep();
    return !!step?.optional;
  }
  return false;
}

function handleSubmit(text) {
  if (!text.trim() && !canSubmitEmpty()) return;
  pushCommandHistory(text);
  logTerminalCommand(text, state.mode);
  dispatchInput(text);
}

async function boot() {
  loadCommandHistory();

  const savedTheme = localStorage.getItem('mysky_assistant_theme');
  if (savedTheme === 'light' || savedTheme === 'vibrant' || savedTheme === 'classic' || savedTheme === 'dark') {
    applyAssistantThemeClass(savedTheme === 'dark' ? 'classic' : savedTheme);
  }

  try {
    const me = await api('/api/me');
    state.agent = me.agent;
    if (isViewer(state.agent)) {
      appendLine('View-only — cannot create or edit.', 'error');
      $('assistantInput').disabled = true;
      return;
    }
    $('assistantAgent').textContent = state.agent.name;
    $('assistantAgent').style.color = state.agent.color || '#38bdf8';

    initAssistantSearchPopup({
      agentId: state.agent.id,
      viewAll: hasPerm(state.agent, 'view_all'),
      onOpenLead: (leadId) => openLeadForManage(leadId),
    });

    initAssistantSplit({
      onToggle: (on) => {
        if (on) appendLine('Split screen on — Leads & Web beside terminal.', 'dim');
      },
    });

    try {
      await fetchKnowledgeEntries(api);
    } catch { /* built-in fallbacks only */ }

    try {
      const settingsRes = await api('/api/settings');
      const settings = settingsRes.settings || {};
      if (settings.sources?.length) state.sources = settings.sources;
      if (settings.tours?.length) state.tourPackages = settings.tours;
      if (settings.visas?.length) state.visaPackages = settings.visas;
      if (settings.visa_costs && typeof settings.visa_costs === 'object') {
        state.visaCosts = settings.visa_costs;
      }
      if (settings.terminal_flow_overrides && typeof settings.terminal_flow_overrides === 'object') {
        setTerminalFlowOverrides(settings.terminal_flow_overrides);
      }
      if (settings.terminal_flow_custom_steps && typeof settings.terminal_flow_custom_steps === 'object') {
        setTerminalFlowCustomSteps(settings.terminal_flow_custom_steps);
      }
      if (settings.terminal_flow_validation_rules && typeof settings.terminal_flow_validation_rules === 'object') {
        setTerminalFlowValidationRules(settings.terminal_flow_validation_rules);
      }
    } catch { /* defaults */ }

    await showBootBriefing();
    refreshGuiIfEnabled();
  } catch {
    location.href = '/';
    return;
  }

  $('assistantForm')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $('assistantInput');
    const text = input?.value || '';
    input.value = '';
    resetCommandHistoryBrowse();
    handleSubmit(text);
    input?.focus();
  });

  $('assistantInput')?.addEventListener('keydown', onAssistantInputKeydown);
  $('assistantInput')?.addEventListener('input', () => {
    if (historyProgrammatic) return;
    if (historyBrowseIndex !== -1) resetCommandHistoryBrowse();
  });

  $('assistantGuiClose')?.addEventListener('click', () => setGuiEnabled(false));

  if (localStorage.getItem('mysky_assistant_gui') === '1') {
    setGuiEnabled(true, { silent: true });
  }

  $('assistantInput')?.focus();
}

boot();
